import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { parseGoogleMapsUrl } from '../lib/urlParser';

function POIDiscovery({ tourId, mapBounds, onPOIsDiscovered, onPOIsSaved, onCustomModeChange, onMapClick, onPreviewMarkerChange }) {
  const [loading, setLoading] = useState(false);
  const [pois, setPOIs] = useState([]);
  const [selectedPOIs, setSelectedPOIs] = useState(new Set());
  const [mode, setMode] = useState('bbox'); // 'bbox', 'route', 'search', 'custom', or 'google-maps'
  const [categoryMode, setCategoryMode] = useState('touristic'); // 'touristic' or 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInBbox, setSearchInBbox] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customPoint, setCustomPoint] = useState({
    name: '',
    description: '',
    lat: '',
    lon: '',
  });
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [filters, setFilters] = useState({
    lang: 'en',
    limit: 100,
  });

  // Notify parent when custom mode changes
  useEffect(() => {
    if (onCustomModeChange) {
      onCustomModeChange(mode === 'custom');
    }
  }, [mode, onCustomModeChange]);

  // Handle map clicks when in custom mode
  useEffect(() => {
    if (mode === 'custom' && onMapClick) {
      const handler = (latlng) => {
        if (latlng) {
          const lat = latlng.lat.toFixed(6);
          const lon = latlng.lng.toFixed(6);
          setCustomPoint((prev) => ({
            ...prev,
            lat,
            lon,
          }));
          // Update preview marker
          if (onPreviewMarkerChange) {
            onPreviewMarkerChange({ lat: parseFloat(lat), lon: parseFloat(lon) });
          }
        }
      };
      // Wrap function in another function for setState
      onMapClick(() => handler);
      
      // Cleanup
      return () => {
        onMapClick(() => null);
      };
    } else if (onMapClick) {
      // Clear handler when not in custom mode
      onMapClick(() => null);
    }
  }, [mode, onMapClick, onPreviewMarkerChange]);

  // Clear preview marker when leaving custom mode
  useEffect(() => {
    if (mode !== 'custom' && mode !== 'google-maps' && onPreviewMarkerChange) {
      onPreviewMarkerChange(null);
    }
  }, [mode, onPreviewMarkerChange]);

  const handleDiscoverBbox = async () => {
    if (!mapBounds) {
      alert('Move the map to set the area for POI discovery');
      return;
    }

    setLoading(true);
    try {
      const bbox = {
        min_lat: mapBounds.getSouth(),
        min_lon: mapBounds.getWest(),
        max_lat: mapBounds.getNorth(),
        max_lon: mapBounds.getEast(),
      };

      const response = await api.discoverPOIsByBbox(bbox, filters.lang, categoryMode, [], filters.limit);
      setPOIs(response.pois || []);
      setSelectedPOIs(new Set());
      if (onPOIsDiscovered) {
        onPOIsDiscovered(response.pois || []);
      }
    } catch (error) {
      alert('Failed to discover POIs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverRoute = async () => {
    if (!tourId) {
      alert('Tour ID is required for route-based discovery');
      return;
    }

    setLoading(true);
    try {
      const response = await api.discoverPOIsByRoute(
        tourId,
        100, // radius_m
        filters.lang,
        categoryMode,
        [],
        200, // sampling_m
        filters.limit
      );
      setPOIs(response.pois || []);
      setSelectedPOIs(new Set());
      if (onPOIsDiscovered) {
        onPOIsDiscovered(response.pois || []);
      }
    } catch (error) {
      alert('Failed to discover POIs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverSearch = async () => {
    if (!searchQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    if (searchInBbox && !mapBounds) {
      alert('Move the map to set search area or disable "Search in current area"');
      return;
    }

    setLoading(true);
    try {
      const bbox = searchInBbox ? {
        min_lat: mapBounds.getSouth(),
        min_lon: mapBounds.getWest(),
        max_lat: mapBounds.getNorth(),
        max_lon: mapBounds.getEast(),
      } : null;

      const response = await api.discoverPOIsBySearch(
        searchQuery,
        bbox,
        filters.lang,
        categoryMode,
        [],
        filters.limit
      );
      setPOIs(response.pois || []);
      setSelectedPOIs(new Set());
      if (onPOIsDiscovered) {
        onPOIsDiscovered(response.pois || []);
      }
    } catch (error) {
      alert('Failed to search POIs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscover = () => {
    if (mode === 'bbox') {
      handleDiscoverBbox();
    } else if (mode === 'route') {
      handleDiscoverRoute();
    } else {
      handleDiscoverSearch();
    }
  };

  const handleAddCustomPoint = async () => {
    if (!tourId) {
      alert('Tour ID is required to add a custom point');
      return;
    }

    if (!customPoint.name.trim()) {
      alert('Please enter a name for the custom point');
      return;
    }

    const lat = parseFloat(customPoint.lat);
    const lon = parseFloat(customPoint.lon);

    if (isNaN(lat) || isNaN(lon)) {
      alert('Please enter valid latitude and longitude');
      return;
    }

    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      alert('Invalid coordinates. Latitude must be between -90 and 90, longitude between -180 and 180');
      return;
    }

    setSaving(true);
    try {
      const request = {
        name: customPoint.name.trim(),
        description: customPoint.description.trim() || null,
        lat: lat,
        lon: lon,
        score: 50,
        tier: 'B',
        radius_m: 50,
        tags: { custom: true },
      };
      
      await api.savePOI(tourId, request);
      alert('Custom point added successfully');
      
      // Reset form and clear preview
      setCustomPoint({ name: '', description: '', lat: '', lon: '' });
      if (onPreviewMarkerChange) {
        onPreviewMarkerChange(null);
      }
      
      if (onPOIsSaved) {
        onPOIsSaved();
      }
    } catch (error) {
      alert('Failed to add custom point: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddFromGoogleMaps = async () => {
    if (!tourId) {
      alert('Tour ID is required to add a point');
      return;
    }

    if (!googleMapsUrl.trim()) {
      alert('Please enter a Google Maps URL');
      return;
    }

    setSaving(true);
    try {
      // Try to parse the Google Maps URL
      const parsed = await parseGoogleMapsUrl(googleMapsUrl, true);
      
      if (!parsed) {
        alert('Could not extract location from the Google Maps URL. Please check the URL format.');
        setSaving(false);
        return;
      }

      if (parsed.error) {
        if (parsed.error === 'resolve_failed') {
          alert('Failed to resolve the shortened URL. Please try using the full Google Maps URL.');
        } else {
          alert('Could not parse the Google Maps URL. ' + (parsed.message || ''));
        }
        setSaving(false);
        return;
      }

      const { lat, lng, name } = parsed;

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        alert('Invalid coordinates extracted from URL');
        setSaving(false);
        return;
      }

      // Update preview marker
      if (onPreviewMarkerChange) {
        onPreviewMarkerChange({ lat, lon: lng });
      }

      // Create the POI request
      const request = {
        name: name || 'Google Maps Location',
        description: googleMapsUrl, // Use the share link as description
        lat: lat,
        lon: lng,
        score: 50,
        tier: 'B',
        radius_m: 50,
        tags: { custom: true, source: 'google-maps' },
      };
      
      await api.savePOI(tourId, request);
      alert('Point from Google Maps added successfully');
      
      // Reset form and clear preview
      setGoogleMapsUrl('');
      if (onPreviewMarkerChange) {
        onPreviewMarkerChange(null);
      }
      
      if (onPOIsSaved) {
        onPOIsSaved();
      }
    } catch (error) {
      alert('Failed to add point from Google Maps: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePOI = (poiId) => {
    const newSelected = new Set(selectedPOIs);
    if (newSelected.has(poiId)) {
      newSelected.delete(poiId);
    } else {
      newSelected.add(poiId);
    }
    setSelectedPOIs(newSelected);
  };

  const handleSelectAll = (tier) => {
    const tierPOIs = pois.filter(poi => poi.tier === tier);
    const newSelected = new Set(selectedPOIs);
    tierPOIs.forEach(poi => newSelected.add(poi.id));
    setSelectedPOIs(newSelected);
  };

  const getTierLabel = (tier) => {
    const labels = {
      'A': 'Landmark',
      'B': 'Notable',
      'C': 'Minor',
    };
    return labels[tier] || tier;
  };

  const getTierColor = (tier) => {
    const colors = {
      'A': '#ff4444',
      'B': '#ff9944',
      'C': '#44aaff',
    };
    return colors[tier] || '#666';
  };

  const groupPOIsByTier = () => {
    const grouped = { A: [], B: [], C: [] };
    pois.forEach(poi => {
      if (grouped[poi.tier]) {
        grouped[poi.tier].push(poi);
      }
    });
    return grouped;
  };

  const groupedPOIs = groupPOIsByTier();

  const handleSaveSelected = async () => {
    if (!tourId) {
      alert('Tour ID is required to save POIs');
      return;
    }

    if (selectedPOIs.size === 0) {
      alert('Please select at least one POI to save');
      return;
    }

    setSaving(true);
    try {
      const selectedPOIList = pois.filter(poi => selectedPOIs.has(poi.id));
      const savePromises = selectedPOIList.map(poi => {
        // Convert POI to SavePOIRequest format
        const request = {
          poi_id: poi.id,
          osm_type: poi.osm_type,
          osm_id: poi.osm_id,
          name: poi.name,
          description: poi.description || null,
          lat: poi.lat,
          lon: poi.lon,
          tags: poi.tags || {},
          wikidata_id: poi.wikidata_id || null,
          score: poi.score,
          tier: poi.tier || null,
        };
        return api.savePOI(tourId, request);
      });

      await Promise.all(savePromises);
      alert(`Successfully saved ${selectedPOIs.size} POI(s) to tour`);
      setSelectedPOIs(new Set());
      
      if (onPOIsSaved) {
        onPOIsSaved();
      }
    } catch (error) {
      alert('Failed to save POIs: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="poi-discovery">
      <div className="card">
        <h3>Discover Spots</h3>
        
        <div className="form-group">
          <label>Category Mode</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className={`button ${categoryMode === 'touristic' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setCategoryMode('touristic')}
              disabled={loading}
            >
              Touristic
            </button>
            <button
              className={`button ${categoryMode === 'all' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setCategoryMode('all')}
              disabled={loading}
            >
              All
            </button>
          </div>
          <p className="help-text">
            {categoryMode === 'touristic' && 'Tourism, historic sites, landmarks, and cultural venues'}
            {categoryMode === 'all' && 'All POI types including shops, amenities, leisure, and nature'}
          </p>
        </div>
        
        <div className="form-group">
          <label>Discovery Mode</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className={`button ${mode === 'bbox' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setMode('bbox')}
              disabled={loading}
            >
              Area
            </button>
            <button
              className={`button ${mode === 'search' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setMode('search')}
              disabled={loading}
            >
              Search
            </button>
            <button
              className={`button ${mode === 'google-maps' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setMode('google-maps')}
              disabled={loading}
            >
              Google Maps
            </button>
            <button
              className={`button ${mode === 'custom' ? 'button-primary' : 'button-secondary'}`}
              onClick={() => setMode('custom')}
              disabled={loading}
            >
              Custom
            </button>
          </div>
          <p className="help-text">
            {mode === 'bbox' && 'Discover POIs in the current map view'}
            {mode === 'search' && 'Search for POIs by name'}
            {mode === 'google-maps' && 'Add a point from Google Maps share link'}
            {mode === 'custom' && 'Add a custom point by clicking on the map'}
          </p>
        </div>

        {mode === 'search' && (
          <>
            <div className="form-group">
              <label>Search Query</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g., Tower Bridge, museum, cathedral..."
                disabled={loading}
                onKeyPress={(e) => e.key === 'Enter' && handleDiscover()}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'block', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={searchInBbox}
                  onChange={(e) => setSearchInBbox(e.target.checked)}
                  disabled={loading}
                  style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}
                />
                <span style={{ verticalAlign: 'middle' }}>Search only in current map area</span>
              </label>
            </div>
          </>
        )}

        {mode === 'google-maps' && (
          <>
            <div className="form-group">
              <label>Google Maps Share Link</label>
              <input
                type="text"
                value={googleMapsUrl}
                onChange={(e) => setGoogleMapsUrl(e.target.value)}
                placeholder="Paste Google Maps URL here (e.g., https://maps.app.goo.gl/... or https://www.google.com/maps/place/...)"
                disabled={saving}
              />
              <p className="help-text">
                Paste a Google Maps share link or place URL. The location name and coordinates will be extracted automatically.
              </p>
            </div>
          </>
        )}

        {mode === 'custom' && (
          <>
            <div className="form-group">
              <label>Point Name</label>
              <input
                type="text"
                value={customPoint.name}
                onChange={(e) => setCustomPoint({ ...customPoint, name: e.target.value })}
                placeholder="e.g., My favorite spot"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={customPoint.description}
                onChange={(e) => setCustomPoint({ ...customPoint, description: e.target.value })}
                placeholder="Brief description of this place..."
                disabled={saving}
                rows="3"
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-group">
              <label>Latitude</label>
              <input
                type="number"
                step="0.000001"
                value={customPoint.lat}
                onChange={(e) => setCustomPoint({ ...customPoint, lat: e.target.value })}
                placeholder="e.g., 51.505"
                disabled={saving}
              />
              <p className="help-text">Latitude coordinate (-90 to 90), or click on the map</p>
            </div>
            <div className="form-group">
              <label>Longitude</label>
              <input
                type="number"
                step="0.000001"
                value={customPoint.lon}
                onChange={(e) => setCustomPoint({ ...customPoint, lon: e.target.value })}
                placeholder="e.g., -0.09"
                disabled={saving}
              />
              <p className="help-text">Longitude coordinate (-180 to 180), or click on the map</p>
            </div>
          </>
        )}

        {mode !== 'custom' && mode !== 'google-maps' && (
          <>
            <div className="form-group">
              <label>Language</label>
              <select
                value={filters.lang}
                onChange={(e) => setFilters({ ...filters, lang: e.target.value })}
                disabled={loading}
              >
                <option value="en">English</option>
                <option value="ru">Russian</option>
              </select>
            </div>

            <div className="form-group">
              <label>Max Results</label>
              <input
                type="number"
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) || 100 })}
                min="10"
                max="500"
                disabled={loading}
              />
            </div>

            <button
              onClick={handleDiscover}
              className="button button-primary"
              disabled={loading || (mode === 'search' && !searchQuery.trim())}
              style={{ width: '100%' }}
            >
              {loading ? 'Discovering...' : mode === 'search' ? 'Search POIs' : 'Discover POIs'}
            </button>
          </>
        )}

        {mode === 'custom' && (
          <button
            onClick={handleAddCustomPoint}
            className="button button-primary"
            disabled={saving || !customPoint.name.trim() || !customPoint.lat || !customPoint.lon}
            style={{ width: '100%' }}
          >
            {saving ? 'Adding...' : 'Add Custom Point'}
          </button>
        )}

        {mode === 'google-maps' && (
          <button
            onClick={handleAddFromGoogleMaps}
            className="button button-primary"
            disabled={saving || !googleMapsUrl.trim()}
            style={{ width: '100%' }}
          >
            {saving ? 'Adding...' : 'Add from Google Maps'}
          </button>
        )}

        {pois.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <p className="help-text">
              Found {pois.length} POIs · {selectedPOIs.size} selected
            </p>
          </div>
        )}
      </div>

      {pois.length > 0 && (
        <div className="card">
          <h4>Discovered POIs</h4>
          
          {['A', 'B', 'C'].map(tier => (
            groupedPOIs[tier].length > 0 && (
              <div key={tier} className="poi-tier-group">
                <div className="poi-tier-header">
                  <h5 style={{ color: getTierColor(tier) }}>
                    {getTierLabel(tier)} ({groupedPOIs[tier].length})
                  </h5>
                  <button
                    className="button button-sm button-text"
                    onClick={() => handleSelectAll(tier)}
                  >
                    Select All
                  </button>
                </div>
                
                <div className="poi-list">
                  {groupedPOIs[tier].map(poi => (
                    <div
                      key={poi.id}
                      className={`poi-item ${selectedPOIs.has(poi.id) ? 'selected' : ''}`}
                      onClick={() => togglePOI(poi.id)}
                    >
                      <div className="poi-item-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPOIs.has(poi.id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="poi-item-content">
                        <div className="poi-item-header">
                          <strong>{poi.name || 'Unnamed'}</strong>
                          <span 
                            className="poi-score-badge"
                            style={{ backgroundColor: getTierColor(poi.tier) }}
                          >
                            {poi.score}
                          </span>
                        </div>
                        {poi.description && (
                          <p className="poi-description">{poi.description}</p>
                        )}
                        <div className="poi-meta">
                          <span className="poi-coords">
                            {poi.lat.toFixed(5)}, {poi.lon.toFixed(5)}
                          </span>
                          {poi.wikidata_id && (
                            <a
                              href={`https://www.wikidata.org/wiki/${poi.wikidata_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="poi-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Wikidata ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {selectedPOIs.size > 0 && (
            <button
              className="button button-primary"
              style={{ width: '100%', marginTop: '1rem' }}
              onClick={handleSaveSelected}
              disabled={saving || !tourId}
            >
              {saving ? 'Saving...' : `Save ${selectedPOIs.size} Selected to Tour`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default POIDiscovery;
